// PartnerPresentation.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Backdrop,
  useTheme,
  styled,
  Avatar,
  Alert
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { useQuery } from 'react-query';

import HomeHeader from '../../Components/HomeHeader';
import HomeFooter from '../../Components/HomeFooter';
import API from '../../API';
import defaultPfp from '../../Components/globalAssets/ppfLogo.png';
import { platforms, platformToFollowing } from '../../Utils/constants';

////////////////////////////////////////////////////////////////////////////////
// 1. Styled Helpers
////////////////////////////////////////////////////////////////////////////////
const Frame = styled('section')({
  position: 'relative',
  width: '100%',
  height: '100vh',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
});

const SlideBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ active }) => ({
  position: 'absolute',
  inset: 0,
  padding: 'clamp(1rem, 4vw, 3rem)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  color: '#fff',
  opacity: active ? 1 : 0,
  transition: 'opacity 0.8s ease'
}));

const NavDots = styled(Box)({
  position: 'absolute',
  bottom: '2rem',
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  gap: 10,
  zIndex: 20
});

const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: active
    ? theme.palette.primary.main
    : theme.palette.primary.main + '55',
  cursor: 'pointer',
  transition: 'transform 0.25s',
  transform: active ? 'scale(1.25)' : 'scale(1)'
}));

////////////////////////////////////////////////////////////////////////////////
// 2. Slides Generator
//
// Now accepts an extra parameter "referrer" to conditionally
// add extra instructions on the final slide.
////////////////////////////////////////////////////////////////////////////////
const makeSlides = (creatorUsername, creatorObj, referrer) => {
  const totalFollowers = creatorObj
    ? platforms.reduce(
        (acc, p) =>
          acc + parseInt(creatorObj.creator[platformToFollowing[p]] || 0, 10),
        0
      )
    : 0;

  return [
    // Personalized creator greeting
    {
      bg: 'linear-gradient(135deg,#6d00ff,#2d0b63)',
      custom: (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {creatorObj ? (
            <>
              <Avatar
                src={creatorObj.creator.pfphref || defaultPfp}
                alt={creatorUsername}
                sx={{ width: 140, height: 140, mb: 1, border: '4px solid #fff' }}
              />
              <Typography
                variant="h2"
                sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: 'clamp(2rem,6vw,3.5rem)' }}
              >
                Hey&nbsp;{creatorUsername}!
              </Typography>
              <Typography variant="h5" sx={{ opacity: 0.85, maxWidth: 700, mb: 1 }}>
                You’ve been invited to join the&nbsp;
                <strong>Blitz Partner Program</strong>
              </Typography>
              <Typography
                variant="body1"
                sx={{ background: '#00000044', px: 2, py: 1, borderRadius: 2, fontWeight: 600 }}
              >
                Total followers&nbsp;•&nbsp;{totalFollowers.toLocaleString()}
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress color="inherit" />
              <Typography variant="h6" sx={{ mt: 2 }}>Loading your profile…</Typography>
            </>
          )}
        </Box>
      )
    },
    // Other slides (unchanged)
    {
      bg: 'linear-gradient(135deg,#ff4d4d,#7000ff)',
      title: 'All‑in‑One AI Sales Manager',
      bullets: [
        'Smart brand matching & outreach 24/7',
        'Automated negotiations → better rates',
        'Zero % manager fees'
      ]
    },
    {
      bg: 'linear-gradient(135deg,#00c9ff,#005bff)',
      title: 'Access the World’s Leading Brands',
      bullets: [
        'Global visibility',
        'Instant e‑contracts & milestone tracking',
        'Portfolio credibility with household names'
      ]
    },
    {
      bg: 'linear-gradient(135deg,#00ff85,#006b4b)',
      title: 'Business Suite Built for Creators',
      bullets: [
        'Contracts, legal & tax tools',
        'BlitzPay instant payouts',
        'Investment & growth insights'
      ]
    },
    {
      bg: 'linear-gradient(135deg,#ffc400,#ff5400)',
      title: 'Increase Revenue 2‑3×',
      bullets: [
        'Data‑backed pricing suggestions',
        'New income channels (events, merch, more)',
        'Turn one‑offs into long‑term retainers'
      ]
    },
    {
      bg: 'linear-gradient(135deg,#9b00ff,#300045)',
      title: 'Get Paid Faster with BlitzPay',
      bullets: [
        'Instant payouts (minutes, not weeks)',
        'Flexible net terms if you prefer',
        'Multi‑currency support'
      ]
    },
    // Final slide with Google Login CTA and extra instructions if a referrer exists.
    {
      bg: 'linear-gradient(135deg,#181818,#000)',
      title: 'Ready to Partner?',
      subtitle: 'Connect your Google account so our AI can start pitching brands from your inbox.',
      cta: true,
      extraInstructions: referrer
        ? `Since you were referred by "${referrer}", your account will automatically be set to Partner mode. 
           Please save your changes on the next page to fully activate your Partner benefits.`
        : null
    }
  ];
};

////////////////////////////////////////////////////////////////////////////////
// 3. Component
////////////////////////////////////////////////////////////////////////////////
const SLIDE_MS = 3500;

export default function PartnerPresentation() {
  const { creator: creatorUsername } = useParams();
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get('referrer'); // Get referrer param from query string
  const navigate = useNavigate();
  const theme = useTheme();

  // Fetch creator details if available
  const {
    data: creatorDetails,
    isLoading: creatorLoading,
    isError: creatorErr
  } = useQuery(
    ['creatorDetails', creatorUsername],
    () => API.creators.fetchDetails(creatorUsername),
    { enabled: !!creatorUsername, refetchOnWindowFocus: false }
  );

  // Carousel slide logic
  const [idx, setIdx] = useState(0);
  const slides = makeSlides(creatorUsername, creatorDetails, referrer);
  const count = slides.length;
  const timerRef = useRef(null);

  const startLoop = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((p) => (p + 1) % count), SLIDE_MS);
  };

  useEffect(() => {
    startLoop();
    return () => clearInterval(timerRef.current);
  }, []);

  const onDotClick = (i) => {
    setIdx(i);
    startLoop();
  };

  // Google connect handler using the unified login route.
  // If a referrer exists, pass it along so the back end can set partner automatically.
  const handleGoogleSuccess = async (response) => {
    try {
      await API.users.unilogin({
        token: response.credential,
        referrer: referrer || undefined
      });
      // Redirect to the edit page; include the referrer param if available.
      navigate(`/creatorconnect/edit?tab=info${referrer ? `&referrer=${referrer}` : ''}`);
    } catch (err) {
      alert(
        err?.response?.data?.error ||
          err.message ||
          'Something went wrong, please try again.'
      );
    }
  };

  const handleGoogleError = () =>
    alert('Google authentication failed, please try again.');

  return (
    <>
      <HomeHeader />

      {creatorErr && (
        <Alert severity="error" sx={{ mb: -4 }}>
          Couldn’t load your creator profile – continuing without it.
        </Alert>
      )}

      {/* Loading backdrop for initial data fetch */}
      <Backdrop
        open={creatorLoading && !creatorDetails}
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Frame>
        {slides.map((s, i) => (
          <SlideBox key={i} active={i === idx} sx={{ background: s.bg, gap: 3 }}>
            {s.custom ? (
              s.custom
            ) : (
              <>
                <Typography
                  variant="h2"
                  sx={{ fontSize: 'clamp(2rem,6vw,3.5rem)', fontWeight: 800, lineHeight: 1.1 }}
                >
                  {s.title}
                </Typography>
                {s.subtitle && (
                  <Typography variant="h5" sx={{ opacity: 0.85, maxWidth: 700, mb: 1 }}>
                    {s.subtitle}
                  </Typography>
                )}
                {s.extraInstructions && (
                  <Typography variant="body2" sx={{ mt: 2, opacity: 0.85, maxWidth: 700 }}>
                    {s.extraInstructions}
                  </Typography>
                )}
                {s.bullets && (
                  <Box
                    component="ul"
                    sx={{
                      listStyle: 'none',
                      p: 0,
                      m: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      maxWidth: 620
                    }}
                  >
                    {s.bullets.map((b) => (
                      <Typography
                        component="li"
                        key={b}
                        sx={{
                          '&::before': {
                            content: '"•"',
                            color: theme.palette.primary.light,
                            mr: 1
                          },
                          fontSize: 'clamp(1rem,2.5vw,1.25rem)'
                        }}
                      >
                        {b}
                      </Typography>
                    ))}
                  </Box>
                )}
                {s.cta && (
                  <>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      text="continue_with"
                      shape="pill"
                      theme="filled_black"
                      size="large"
                      ux_mode="popup"
                    />
                    <Typography variant="caption" sx={{ mt: 2, opacity: 0.7 }}>
                      We’ll only use scoped Gmail access to send/receive brand emails on your behalf. You can revoke anytime.
                    </Typography>
                  </>
                )}
              </>
            )}
          </SlideBox>
        ))}

        {/* Navigation dots */}
        <NavDots>
          {Array.from({ length: count }).map((_, i) => (
            <Dot key={i} active={i === idx} onClick={() => onDotClick(i)} />
          ))}
        </NavDots>
      </Frame>

      <HomeFooter />
    </>
  );
}
